import { useEffect } from 'react';

import { AxiosError } from 'axios';
import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';

import { fetchCommonEventData } from 'api/events';
import {
  fetchEventSession,
  fetchSessionSpeakers,
  fetchPresentationsCount,
  fetchVideoSession,
  fetchSessionPresentations,
  fetchSessionSpeakersCount,
} from 'api/session';
import { parseQuery } from 'api/utils';
import {
  EventStandardContainer,
  EventStandardContainerData,
} from 'components/EventStandardContainer';
import { Props as MetaTagsProps } from 'components/MetaTags';
import { SingleSessionContainer } from 'containers/SingleSession';
import { LiveSessionProvider } from 'containers/SingleSession/context/LiveSessionContext';
import { RtmpProvider } from 'containers/SingleSession/Rtmp/RtmpProvider';
import { useAuthContext } from 'store';
import { handleAxiosError, IError } from 'utils/handleAxiosError';
import { useFeatureFlagContext } from 'utils/unleashFeatureFlags/FeatureFlagProvider';
import { FEATURE_FLAGS } from 'utils/unleashFeatureFlags/flags';

type Session = ResolvedPromise<ReturnType<typeof fetchEventSession>>;
type Presenters = ResolvedPromise<ReturnType<typeof fetchSessionSpeakers>>;
type Presentations = ResolvedPromise<ReturnType<typeof fetchSessionPresentations>>;
export type VideoSession = ResolvedPromise<ReturnType<typeof fetchVideoSession>>;

// export type SingleSessionPageProps = InferGetStaticPropsType<typeof getStaticProps>;
export type SingleSessionPageProps = InferGetServerSidePropsType<typeof getServerSideProps>;

const dummyfySession = async (sessionData: MorressierSession) => {
  // minimum required data for session
  const response = parseQuery({
    _id: sessionData._id,
    event: sessionData.event,
    description: sessionData.description,
    end_date: sessionData.end_date,
    start_date: sessionData.start_date,
    name: sessionData.name,
    title: sessionData?.name || sessionData?.title,
    banner_url: sessionData.banner_url,
    main_stage: {
      id: '',
      is_live: false,
      live_status: sessionData?.main_stage?.live_status || 'NOT_STARTED',
      participants: [],
      presentations: [],
      is_allowed_to_delegate: false,
      delegate_join_requests: [],
    },
    posters: [],
    backstage: { participants: [] },
    sponsors: sessionData.sponsors,
    stream_type: sessionData.stream_type,
  }) as unknown as MorressierSession;

  return response;
};

const fetchSessionPageData = async (eventId: string, sessionId: string) => {
  const [session, presentersCount, presentationsCount, videoSession] = await Promise.all([
    fetchEventSession(eventId, sessionId, { include: 'ads' }),
    fetchSessionSpeakersCount(eventId, sessionId),
    fetchPresentationsCount(eventId, sessionId),
    fetchVideoSession(sessionId, 'session'),
  ]);

  return {
    session,
    presentersCount,
    presentationsCount,
    videoSession,
  };
};

const fetchSessionPageFakeData = async (eventId: string, sessionId: string) => {
  const [session, presentersCount, presentationsCount, videoSession] = await Promise.all([
    dummyfySession(await fetchEventSession(eventId, sessionId, { include: 'ads' })),
    (() => ({ count: 0 } as ResolvedPromise<ReturnType<typeof fetchSessionSpeakersCount>>))(),
    (() => ({ count: 0 } as ResolvedPromise<ReturnType<typeof fetchPresentationsCount>>))(),
    (() => null as ResolvedPromise<ReturnType<typeof fetchVideoSession>>)(),
  ]);

  return {
    session,
    presentersCount,
    presentationsCount,
    videoSession,
  };
};

const SingleSession: NextPage<SingleSessionPageProps> = ({ data, error }) => {
  const { unleash } = useFeatureFlagContext();
  const { state: authState } = useAuthContext();

  useEffect(() => {
    if (unleash && authState) {
      unleash.updateContext({ userId: authState.profile?._id || authState.profile?.id });
    }
  }, [unleash, authState]);

  return (
    <LiveSessionProvider session={data?.session as MorressierSession}>
      <RtmpProvider streamType={data?.session.stream_type || 'MORRESSIER_STREAM'}>
        <EventStandardContainer
          data={data}
          error={error}
          Component={SingleSessionContainer}
          metaTags={data?.metaTags}
          fetchEmbargoedPageData={
            data?.event.embargoed
              ? () => fetchSessionPageData(data!.event._id, data!.session._id)
              : undefined
          }
        />
      </RtmpProvider>
    </LiveSessionProvider>
  );
};

export const getServerSideProps: GetServerSideProps<
  {
    data?: EventStandardContainerData<{
      session: Session;
      presentersCount: { count: number };
      presentationsCount: { count: number };
      videoSession: VideoSession;
      metaTags: MetaTagsProps;
    }>;
    error?: IError;
  },
  { eventId: string; sessionId: string }
> = async ctx => {
  try {
    const eventId = ctx.params?.eventId as string;
    const sessionId = ctx.params?.sessionId as string;

    const { eventInfo, tabsConfig } = await fetchCommonEventData(eventId);

    const { session, presentersCount, presentationsCount, videoSession } = eventInfo.event.embargoed
      ? await fetchSessionPageFakeData(eventId, sessionId)
      : await fetchSessionPageData(eventId, sessionId);

    return {
      props: {
        data: {
          ...eventInfo,
          tabsConfig,
          session,
          presentersCount,
          presentationsCount,
          videoSession,
          metaTags: {
            title: session.name,
          },
        },
      },
    };
  } catch (err) {
    const error = handleAxiosError(err as AxiosError);

    return {
      props: { error },
    };
  }
};

export default SingleSession;
